import type { JobPortalOfferDetail } from '@eliadmin/core/src/api/jobportal/offers-api';
import type { EditJobOfferFormValues } from './edit-job-offer-form';

// Additional fields exposed by the store Offer DetailResource but not yet
// represented in the installed core's offer type.
type EditableOffer = JobPortalOfferDetail & {
  benefit?: string | null;
  company_name?: string | null;
  qualification?: string | null;
  personality_assumption?: string | null;
  contact_title_before_name?: string | null;
  contact_title_after_name?: string | null;
};

type CompanyDefaults = {
  phone?: string | null;
  email?: string | null;
  contact_name?: string | null;
  contact_surname?: string | null;
  name?: string | null;
};

type TextField = { id: number; value: string | null };

function getTextFields(offer: EditableOffer): TextField[] {
  return (offer.text_fields ?? []).map(field => {
    const item = field as TextField;
    return { id: item.id, value: item.value };
  });
}

export function getEditJobOfferValues(
  offer: EditableOffer,
  company?: CompanyDefaults,
): EditJobOfferFormValues {
  return {
    name: offer.name ?? '',
    description: offer.description ?? '',
    benefit: offer.benefit ?? '',
    qualification: offer.qualification ?? '',
    categories:
      offer.categories?.map(category => ({ label: category.name, value: category.id })) ?? [],
    contact_phone: offer.contact_phone ?? company?.phone ?? '',
    contact_email: offer.contact_email ?? company?.email ?? '',
    contact_name: offer.contact_name ?? company?.contact_name ?? '',
    contact_surname: offer.contact_surname ?? company?.contact_surname ?? '',
    company_name: offer.company_name ?? offer.company?.name ?? company?.name ?? '',
    country: offer.country?.id ?? '',
    min_salary: String(offer.min_salary ?? ''),
    max_salary: String(offer.max_salary ?? ''),
    skills: offer.skills ?? [],
    languages:
      offer.languages?.map(item => ({
        language: item.language.id,
        level: item.level,
      })) ?? [],
    driving_licences: offer.driving_licences ?? [],
    hasDrivingLicence: !!offer.driving_licences?.length,
    employment_types: offer.employment_type?.id?.toString() ?? '',
    web_url: offer.web_url ?? '',
    free_places: String(offer.free_places ?? 1),
    address: offer.address ?? '',
    street_number: offer.street_number ?? '',
    postcode: offer.postcode ?? '',
    city: offer.city ?? '',
    salary_type: offer.salary_type ?? null,
    workplace: offer.workplace ?? null,
    suitability: offer.suitability ?? null,
    age_range: offer.age_range ?? null,
    graduate: offer.graduate ?? null,
    discipline: offer.discipline ?? null,
    prerequisite: offer.prerequisite ?? null,
    homeoffice: offer.values?.some(value => value.id === 3) ?? false,
    for_graduates: offer.values?.some(value => value.id === 1) ?? false,
    for_foreigners: offer.values?.some(value => value.id === 2) ?? false,
    dual_education: offer.tags?.some(tag => tag.id === 4) ?? false,
    date_start: offer.date_start ? new Date(offer.date_start) : null,
    video: null,
    video_current: offer.company_contact_url ? { id: offer.company_contact_url } : null,
    company_contact_url: offer.company_contact_url ?? null,
    tags: offer.tags ?? [],
    text_fields: getTextFields(offer),
    start_immediately: getTextFields(offer).some(
      field => field.id === 1 && ['true', '1'].includes(field.value ?? ''),
    ),
  };
}

export function transformEditJobOfferData(data: EditJobOfferFormValues, offer: EditableOffer) {
  const values = (offer.values ?? []).filter(value => ![1, 2, 3].includes(value.id));
  if (data.for_graduates) values.push({ id: 1, name: '' });
  if (data.for_foreigners) values.push({ id: 2, name: '' });
  if (data.homeoffice) values.push({ id: 3, name: '' });

  const tags = (offer.tags ?? []).filter(tag => tag.id !== 4).map(tag => ({ id: tag.id }));
  if (data.dual_education) tags.push({ id: 4 });
  const textFields = getTextFields(offer).filter(field => field.id !== 1);
  if (data.start_immediately) textFields.push({ id: 1, value: 'true' });

  // Updating an offer is a full replacement in OfferRequest/OfferService.
  // Carry through fields with no control in this project so editing one field
  // does not erase unrelated content or relationships.
  return {
    ...data,
    short_description: offer.short_description,
    qualification: data.qualification ?? offer.qualification,
    personality_assumption: offer.personality_assumption,
    contact_title_before_name: offer.contact_title_before_name,
    contact_title_after_name: offer.contact_title_after_name,
    image: offer.image,
    gender: offer.gender,
    images: offer.images,
    attachments: offer.attachments,
    working_hours: offer.working_hours,
    categories: (data.categories ?? []).map((category: { value: number }) => ({
      id: category.value,
    })),
    country: data.country ? { id: data.country } : null,
    employment_type: data.employment_types
      ? { id: Number(data.employment_types) }
      : data.dual_education
        ? { id: 5 }
        : null,
    min_salary: (data.min_salary || '0').trim().replace(',', '.'),
    max_salary: (data.max_salary || '0').trim().replace(',', '.'),
    // Store validation currently uses CV skill IDs; omit IDs as the existing
    // create mapper does, while preserving the editable name/level values.
    skills: (data.skills ?? [])
      .filter((skill: { name?: string }) => skill.name)
      .map((skill: { name: string; level?: string }) => ({
        name: skill.name,
        level: skill.level || null,
      })),
    languages: (data.languages ?? [])
      .filter((item: { language?: number | string }) => item.language)
      .map((item: { language: number | string; level?: string }) => ({
        language: { id: Number(item.language) },
        level: item.level || null,
      })),
    driving_licences: data.hasDrivingLicence
      ? (data.driving_licences ?? []).map((licence: { id: number }) => ({ id: licence.id }))
      : [],
    values: values.map(value => ({ id: value.id })),
    tags,
    text_fields: textFields,
  };
}
